import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { GlobalToastTypeNames, ToastTypes } from 'types';
import { useTriggerCommsSyncMutation } from 'core/graphql';

import { useToasts } from '../Toast/useToasts';

interface IUseCommsRefresh {
  refresh: () => Promise<void>;
  syncing: boolean;
}

/**
 * Shared "refresh messages" action: enqueues a Slack backfill PLUS a Gmail
 * backfill and toasts the outcome. Without groupId the backend syncs the
 * enabled workspaces of ACTIVE groups only (groups with future lessons) +
 * Gmail; with groupId it syncs that group's workspace regardless — so the
 * per-group button also works for finished groups. Async via Celery — new
 * messages appear once the worker finishes. Used by the dashboard (active
 * groups) and the group communication tab (that group). The student card
 * keeps the mail-only useGmailRefresh.
 */
export const useCommsRefresh = (groupId?: string): IUseCommsRefresh => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const [syncComms, { loading: syncing }] = useTriggerCommsSyncMutation();

  const refresh = useCallback(async () => {
    let ok = false;
    try {
      const res = await syncComms({
        variables: groupId ? { groupId } : {},
      });
      ok = Boolean(res.data?.syncCommunicationsNow?.success);
    } catch {
      ok = false;
    }
    addToast({
      data: {
        type: ok ? ToastTypes.success : ToastTypes.error,
        title: t(
          ok
            ? 'pages.communications.commsSyncQueued'
            : 'pages.communications.commsSyncFailed',
        ),
      },
      type: GlobalToastTypeNames.FakturowniaMatch,
    });
  }, [syncComms, groupId, addToast, t]);

  return { refresh, syncing };
};
